import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useLanguageStore } from '../../store/languageStore';
import { usePagedPatients, useSearchPatients, useCreatePatient, useUpdatePatient, useDeletePatient, usePatient } from '../../hooks/usePatients';
import { PatientList } from '../../components/patients/PatientList';
import { PatientSearch } from '../../components/patients/PatientSearch';
import { PatientForm } from '../../components/patients/PatientForm';
import { PageHeader } from '../../components/common/PageHeader';
import { Modal } from '../../components/common/Modal';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { Button } from '../../components/common/Button';
import { Plus, Trash2, Users, Edit, Eye, Calendar } from 'lucide-react';
import type { CreatePatientDto, UpdatePatientDto } from '../../types/patient.types';
import toast from 'react-hot-toast';

export const PatientsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isRTL } = useLanguageStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editPatientId, setEditPatientId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [deletePatientId, setDeletePatientId] = useState<number | null>(null);
  const [viewMode] = useState<'cards' | 'table'>('table');
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortDescending, setSortDescending] = useState(false);

  // Use paged patients for table view
  const { data: pagedData, isLoading: isLoadingPaged, error: pagedError, refetch: refetchPaged } = usePagedPatients({
    pageNumber,
    pageSize,
    searchQuery: searchQuery || undefined,
    sortBy,
    sortDescending,
  });

  // Use search for cards view (keep existing behavior)
  const { data: searchResults, isLoading: isLoadingSearch, error: searchError, refetch: refetchSearch } = useSearchPatients(searchQuery);
  const createMutation = useCreatePatient();
  const updateMutation = useUpdatePatient();
  const deleteMutation = useDeletePatient();
  const { data: editPatient, isLoading: isLoadingEditPatient } = usePatient(editPatientId || 0);

  // Reset to page 1 when search query changes
  useEffect(() => {
    setPageNumber(1);
  }, [searchQuery]);

  // For table view, use paged data. For cards view, use search results or empty array
  const patients = viewMode === 'table' 
    ? (pagedData?.items || []) 
    : (searchQuery ? (searchResults || []) : []);
  const isLoading = viewMode === 'table' ? isLoadingPaged : isLoadingSearch;
  const error = viewMode === 'table' ? pagedError : searchError;

  const handleCreatePatient = async (data: CreatePatientDto) => {
    try {
      await createMutation.mutateAsync(data);
      toast.success(t('patients.patientCreated', 'Patient created successfully'));
      setIsModalOpen(false);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error 
        ? error.message 
        : t('patients.createFailed', 'Failed to create patient');
      toast.error(errorMessage);
    }
  };

  const handleUpdatePatient = async (data: UpdatePatientDto) => {
    if (!editPatientId) return;

    try {
      await updateMutation.mutateAsync({ id: editPatientId, data });
      toast.success(t('patients.patientUpdated', 'Patient updated successfully'));
      setIsEditModalOpen(false);
      setEditPatientId(null);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error 
        ? error.message 
        : t('patients.updateFailed', 'Failed to update patient');
      toast.error(errorMessage);
    }
  };

  const handleDeletePatient = async () => {
    if (deletePatientId) {
      try {
        await deleteMutation.mutateAsync(deletePatientId);
        toast.success(t('patients.patientDeleted', 'Patient deleted successfully'));
        setDeletePatientId(null);
      } catch (error: unknown) {
        const errorMessage = error instanceof Error 
          ? error.message 
          : t('patients.deleteFailed', 'Failed to delete patient');
        toast.error(errorMessage);
        console.error('Error deleting patient:', error);
        setDeletePatientId(null);
      }
    }
  };

  return (
    <div className="w-100" style={{ maxWidth: '100%', overflowX: 'hidden' }} dir={isRTL ? 'rtl' : 'ltr'}>
      <PageHeader
        title={t('patients.title')}
        actions={
          <div className={`d-flex gap-3 align-items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Button 
              onClick={() => navigate('/visits/new')} 
              variant="success"
              className="d-flex align-items-center gap-2"
              style={{ direction: isRTL ? 'rtl' : 'ltr' }}
            >
              <Calendar size={16} />
              {t('visits.addVisit', 'إضافة زيارة جديدة')}
            </Button>
            <div style={{ marginLeft: isRTL ? '0' : '0.5rem', marginRight: isRTL ? '0.5rem' : '0' }}>
              <Button 
                onClick={() => setIsModalOpen(true)} 
                variant="primary"
                className="d-flex align-items-center gap-2"
                style={{ direction: isRTL ? 'rtl' : 'ltr' }}
              >
                <Plus size={16} />
                {t('patients.addPatient')}
              </Button>
            </div>
          </div>
        }
      />

      <div className="row">
        <div className="col-12">
          <PatientSearch onSearch={setSearchQuery} />
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          {viewMode === 'table' ? (
            <div className="card shadow-sm border-0">
              <div className="card-body p-0">
                {isLoading ? (
                  <div className="d-flex justify-content-center align-items-center py-5">
                    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                      <span className="visually-hidden">{t('common.loading')}</span>
                    </div>
                  </div>
                ) : error ? (
                  <div className="text-center py-5">
                    <div className="alert alert-danger" role="alert">
                      <strong>{t('common.error', 'Error')}:</strong> {error.message || t('common.error', 'An error occurred')}
                      <div className="mt-3">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => refetchPaged()}
                        >
                          {t('common.retry', 'Try again')}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : patients.length === 0 ? (
                  <div className="text-center py-5">
                    <p className="text-muted mb-0" style={{ fontSize: '1.1rem' }}>{t('patients.noPatients', 'No patients found')}</p>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-modern mb-0" dir={isRTL ? 'rtl' : 'ltr'} style={{ 
                        backgroundColor: '#ffffff',
                        borderRadius: '0.5rem',
                        overflow: 'hidden'
                      }}>
                        <thead style={{ 
                          backgroundColor: 'rgba(59, 130, 246, 0.1)',
                          borderBottom: '2px solid rgba(59, 130, 246, 0.2)'
                        }}>
                          <tr>
                            <th 
                              className={isRTL ? 'pe-3' : 'ps-3'} 
                              style={{ 
                                textAlign: 'center', 
                                width: '60px',
                                fontWeight: '600',
                                color: '#1e40af',
                                padding: '1rem 0.75rem',
                                fontSize: '0.875rem'
                              }}
                            >
                              {t('common.id', 'ID')}
                            </th>
                            <th
                              style={{ 
                                textAlign: isRTL ? 'right' : 'left',
                                cursor: 'pointer', 
                                userSelect: 'none',
                                fontWeight: '600',
                                color: '#1e40af',
                                padding: '1rem 0.75rem',
                                fontSize: '0.875rem',
                                transition: 'all 0.2s ease'
                              }}
                              onClick={() => {
                                if (sortBy === 'name') {
                                  setSortDescending(!sortDescending);
                                } else {
                                  setSortBy('name');
                                  setSortDescending(false);
                                }
                                setPageNumber(1);
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.15)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              {t('patients.name', 'Name')}
                              {sortBy === 'name' && (sortDescending ? ' ↓' : ' ↑')}
                            </th>
                            <th
                              style={{ 
                                textAlign: isRTL ? 'right' : 'left',
                                cursor: 'pointer', 
                                userSelect: 'none',
                                fontWeight: '600',
                                color: '#1e40af',
                                padding: '1rem 0.75rem',
                                fontSize: '0.875rem',
                                transition: 'all 0.2s ease'
                              }}
                              onClick={() => {
                                if (sortBy === 'age') {
                                  setSortDescending(!sortDescending);
                                } else {
                                  setSortBy('age');
                                  setSortDescending(false);
                                }
                                setPageNumber(1);
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.15)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              {t('patients.age', 'Age')}
                              {sortBy === 'age' && (sortDescending ? ' ↓' : ' ↑')}
                            </th>
                            <th style={{ 
                              textAlign: isRTL ? 'right' : 'left',
                              fontWeight: '600',
                              color: '#1e40af',
                              padding: '1rem 0.75rem',
                              fontSize: '0.875rem'
                            }}>
                              {t('patients.phone', 'Phone')}
                            </th>
                            <th style={{ 
                              textAlign: isRTL ? 'right' : 'left',
                              fontWeight: '600',
                              color: '#1e40af',
                              padding: '1rem 0.75rem',
                              fontSize: '0.875rem'
                            }}>
                              {t('patients.gender', 'Gender')}
                            </th>
                            <th 
                              className={`text-center ${isRTL ? 'ps-3' : 'pe-3'}`}
                              style={{ 
                                fontWeight: '600',
                                color: '#1e40af',
                                padding: '1rem 0.75rem',
                                fontSize: '0.875rem'
                              }}
                            >
                              {t('common.actions', 'Actions')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {patients.map((patient, index) => (
                            <tr
                              key={patient.id}
                              onClick={() => navigate(`/patients/${patient.id}`)}
                              style={{ 
                                cursor: 'pointer',
                                backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc',
                                transition: 'all 0.2s ease'
                              }}
                              className="table-row-modern"
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#eff6ff';
                                e.currentTarget.style.transform = isRTL ? 'translateX(-2px)' : 'translateX(2px)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f8fafc';
                                e.currentTarget.style.transform = 'translateX(0)';
                              }}
                            >
                              <td className={`text-center ${isRTL ? 'pe-3' : 'ps-3'}`} style={{ padding: '1rem 0.75rem' }}>
                                <span style={{ 
                                  color: '#64748b',
                                  fontWeight: '600',
                                  fontSize: '0.875rem'
                                }}>
                                  {patient.id}
                                </span>
                              </td>
                              <td style={{ padding: '1rem 0.75rem' }}>
                                <div className="d-flex align-items-center gap-2">
                                  <div 
                                    className="rounded-circle d-flex align-items-center justify-content-center text-white" 
                                    style={{ 
                                      width: '32px', 
                                      height: '32px', 
                                      minWidth: '32px', 
                                      flexShrink: 0,
                                      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                      boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)'
                                    }}
                                  >
                                    <Users size={16} />
                                  </div>
                                  <div className={`flex-grow-1 ${isRTL ? 'text-end' : 'text-start'}`}>
                                    <div style={{ 
                                      fontWeight: '600',
                                      color: '#1e293b',
                                      fontSize: '0.9375rem'
                                    }}>
                                      {patient.name}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td style={{ padding: '1rem 0.75rem' }}>
                                <span style={{ 
                                  color: '#64748b',
                                  fontSize: '0.875rem'
                                }}>
                                  {patient.age} {t('patients.years', 'years')}
                                </span>
                              </td>
                              <td style={{ padding: '1rem 0.75rem' }}>
                                <span style={{ 
                                  color: '#64748b',
                                  fontSize: '0.875rem'
                                }}>
                                  {patient.phone}
                                </span>
                              </td>
                              <td style={{ padding: '1rem 0.75rem' }}>
                                <span 
                                  className="badge"
                                  style={{
                                    backgroundColor: patient.gender === 'Male' ? '#3b82f6' : '#ef4444',
                                    color: '#ffffff',
                                    padding: '0.375rem 0.75rem',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.75rem',
                                    fontWeight: '500'
                                  }}
                                >
                                  {patient.gender === 'Male' ? t('patients.male', 'Male') : t('patients.female', 'Female')}
                                </span>
                              </td>
                              <td className={`text-center ${isRTL ? 'ps-3' : 'pe-3'}`} style={{ padding: '1rem 0.75rem' }}>
                                <div className={`d-flex gap-2 justify-content-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={(e) => {
                                      e?.stopPropagation();
                                      navigate(`/patients/${patient.id}`);
                                    }}
                                    className="p-2"
                                    title={t('patients.view', 'عرض التفاصيل')}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="success"
                                    size="sm"
                                    onClick={(e) => {
                                      e?.stopPropagation();
                                      navigate(`/visits/new?patientId=${patient.id}`);
                                    }}
                                    className="p-2"
                                    title={t('visits.addVisit', 'إضافة زيارة جديدة')}
                                  >
                                    <Calendar className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={(e) => {
                                      e?.stopPropagation();
                                      setEditPatientId(patient.id);
                                      setIsEditModalOpen(true);
                                    }}
                                    className="p-2"
                                    title={t('patients.edit', 'تعديل')}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={(e) => {
                                      e?.stopPropagation();
                                      setDeletePatientId(patient.id);
                                    }}
                                    className="p-2"
                                    title={t('patients.delete', 'حذف')}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {pagedData && pagedData.totalCount > 0 && (
                      <div 
                        className="px-4 py-3"
                        style={{
                          backgroundColor: '#F3F8FF',
                          borderTop: '2px solid rgba(59, 130, 246, 0.2)',
                          borderRadius: '0 0 0.75rem 0.75rem',
                          boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.04)'
                        }}
                      >
                        <div className={`d-flex ${isRTL ? 'flex-row-reverse' : ''} justify-content-between align-items-center gap-3`} style={{ flexWrap: 'nowrap' }}>
                          <div 
                            className={`table-pagination-info ${isRTL ? 'text-end' : 'text-start'}`}
                            style={{
                              color: '#64748b',
                              fontSize: '0.875rem',
                              fontWeight: '500',
                              flexShrink: 0,
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {t('table.showing', 'Showing')} <strong style={{ color: '#1e293b', fontWeight: '600' }}>{(pageNumber - 1) * pageSize + 1}</strong> - <strong style={{ color: '#1e293b', fontWeight: '600' }}>{Math.min(pageNumber * pageSize, pagedData.totalCount)}</strong> {t('table.of', 'of')} <strong style={{ color: '#3b82f6', fontWeight: '600' }}>{pagedData.totalCount}</strong>
                          </div>
                          <div 
                            className={`d-flex align-items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}
                            style={{
                              flex: '1 1 auto',
                              justifyContent: 'center',
                              flexShrink: 1,
                              minWidth: 0
                            }}
                          >
                            <button
                              type="button"
                              onClick={() => setPageNumber(p => Math.max(1, p - 1))}
                              disabled={!pagedData.hasPreviousPage}
                              aria-label={t('table.previous', 'Previous')}
                              className="btn"
                              style={{
                                height: '2.5rem',
                                padding: '0 1rem',
                                backgroundColor: !pagedData.hasPreviousPage ? '#e5e7eb' : '#3b82f6',
                                color: !pagedData.hasPreviousPage ? '#9ca3af' : '#ffffff',
                                border: 'none',
                                borderRadius: '0.75rem',
                                fontSize: '0.875rem',
                                fontWeight: '600',
                                cursor: !pagedData.hasPreviousPage ? 'not-allowed' : 'pointer',
                                boxShadow: !pagedData.hasPreviousPage ? 'none' : '0 1px 3px rgba(59, 130, 246, 0.3)',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}
                              onMouseEnter={(e) => {
                                if (pagedData.hasPreviousPage) {
                                  e.currentTarget.style.backgroundColor = '#2563eb';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                  e.currentTarget.style.boxShadow = '0 2px 6px rgba(59, 130, 246, 0.4)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (pagedData.hasPreviousPage) {
                                  e.currentTarget.style.backgroundColor = '#3b82f6';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(59, 130, 246, 0.3)';
                                }
                              }}
                              onMouseDown={(e) => {
                                if (pagedData.hasPreviousPage) {
                                  e.currentTarget.style.transform = 'translateY(0) scale(0.98)';
                                }
                              }}
                              onMouseUp={(e) => {
                                if (pagedData.hasPreviousPage) {
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }
                              }}
                            >
                              {isRTL ? '→' : '←'} {t('table.previous', 'Previous')}
                            </button>
                            {pagedData.totalPages > 1 && (() => {
                              const pages: (number | string)[] = [];
                              const totalPages = pagedData.totalPages;
                              const currentPage = pageNumber;
                              
                              let startPage = Math.max(1, currentPage - 2);
                              let endPage = Math.min(totalPages, currentPage + 2);
                              
                              if (currentPage <= 3) {
                                endPage = Math.min(5, totalPages);
                              }
                              if (currentPage >= totalPages - 2) {
                                startPage = Math.max(1, totalPages - 4);
                              }
                              
                              if (startPage > 1) {
                                pages.push(1);
                                if (startPage > 2) {
                                  pages.push('...');
                                }
                              }
                              
                              for (let i = startPage; i <= endPage; i++) {
                                pages.push(i);
                              }
                              
                              if (endPage < totalPages) {
                                if (endPage < totalPages - 1) {
                                  pages.push('...');
                                }
                                pages.push(totalPages);
                              }
                              
                              return (
                                <div className="d-flex align-items-center gap-1">
                                  {pages.map((page, idx) => {
                                    if (page === '...') {
                                      return (
                                        <span 
                                          key={`ellipsis-${idx}`}
                                          style={{ 
                                            padding: '0 0.5rem',
                                            color: '#94a3b8',
                                            fontSize: '0.875rem',
                                            fontWeight: '500'
                                          }}
                                        >
                                          ...
                                        </span>
                                      );
                                    }
                                    
                                    const pageNum = page as number;
                                    const isActive = pageNum === currentPage;
                                    
                                    return (
                                      <button
                                        key={pageNum}
                                        type="button"
                                        onClick={() => setPageNumber(pageNum)}
                                        aria-label={`${t('table.page', 'Page')} ${pageNum}`}
                                        aria-current={isActive ? 'page' : undefined}
                                        style={{
                                          minWidth: '2.5rem',
                                          height: '2.5rem',
                                          padding: '0',
                                          border: isActive ? 'none' : '1px solid #e2e8f0',
                                          borderRadius: '50%',
                                          backgroundColor: isActive ? '#3b82f6' : 'transparent',
                                          color: isActive ? '#ffffff' : '#64748b',
                                          fontSize: '0.875rem',
                                          fontWeight: isActive ? '600' : '500',
                                          cursor: 'pointer',
                                          transition: 'all 0.2s ease',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          boxShadow: isActive ? '0 2px 4px rgba(59, 130, 246, 0.3)' : 'none'
                                        }}
                                        onMouseEnter={(e) => {
                                          if (!isActive) {
                                            e.currentTarget.style.backgroundColor = '#f1f5f9';
                                            e.currentTarget.style.borderColor = '#cbd5e1';
                                          } else {
                                            e.currentTarget.style.backgroundColor = '#2563eb';
                                            e.currentTarget.style.boxShadow = '0 2px 6px rgba(59, 130, 246, 0.4)';
                                          }
                                        }}
                                        onMouseLeave={(e) => {
                                          if (!isActive) {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                            e.currentTarget.style.borderColor = '#e2e8f0';
                                          } else {
                                            e.currentTarget.style.backgroundColor = '#3b82f6';
                                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.3)';
                                          }
                                        }}
                                      >
                                        {pageNum}
                                      </button>
                                    );
                                  })}
                                </div>
                              );
                            })()}
                            <button
                              type="button"
                              onClick={() => setPageNumber(p => Math.min(pagedData.totalPages, p + 1))}
                              disabled={!pagedData.hasNextPage}
                              aria-label={t('table.next', 'Next')}
                              className="btn"
                              style={{
                                height: '2.5rem',
                                padding: '0 1rem',
                                backgroundColor: !pagedData.hasNextPage ? '#e5e7eb' : '#3b82f6',
                                color: !pagedData.hasNextPage ? '#9ca3af' : '#ffffff',
                                border: 'none',
                                borderRadius: '0.75rem',
                                fontSize: '0.875rem',
                                fontWeight: '600',
                                cursor: !pagedData.hasNextPage ? 'not-allowed' : 'pointer',
                                boxShadow: !pagedData.hasNextPage ? 'none' : '0 1px 3px rgba(59, 130, 246, 0.3)',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}
                              onMouseEnter={(e) => {
                                if (pagedData.hasNextPage) {
                                  e.currentTarget.style.backgroundColor = '#2563eb';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                  e.currentTarget.style.boxShadow = '0 2px 6px rgba(59, 130, 246, 0.4)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (pagedData.hasNextPage) {
                                  e.currentTarget.style.backgroundColor = '#3b82f6';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(59, 130, 246, 0.3)';
                                }
                              }}
                              onMouseDown={(e) => {
                                if (pagedData.hasNextPage) {
                                  e.currentTarget.style.transform = 'translateY(0) scale(0.98)';
                                }
                              }}
                              onMouseUp={(e) => {
                                if (pagedData.hasNextPage) {
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }
                              }}
                            >
                              {t('table.next', 'Next')} {isRTL ? '←' : '→'}
                            </button>
                          </div>
                          <div 
                            className={`d-flex align-items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}
                            style={{
                              flexShrink: 0,
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <label 
                              htmlFor="page-size-select"
                              style={{
                                color: '#64748b',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                margin: 0,
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {t('table.rowsPerPage', 'Rows per page')}:
                            </label>
                            <select
                              id="page-size-select"
                              value={pageSize}
                              onChange={(e) => {
                                setPageSize(Number(e.target.value));
                                setPageNumber(1);
                              }}
                              aria-label={t('table.rowsPerPage', 'Rows per page')}
                              style={{
                                height: '2.5rem',
                                padding: '0 2.5rem 0 0.75rem',
                                border: '1px solid #e2e8f0',
                                borderRadius: '0.5rem',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                color: '#1e293b',
                                backgroundColor: '#ffffff',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                outline: 'none',
                                minWidth: '5rem',
                                appearance: 'none',
                                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748b' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: isRTL ? '0.5rem center' : 'calc(100% - 0.5rem) center',
                                paddingRight: isRTL ? '0.75rem' : '2.5rem',
                                paddingLeft: isRTL ? '2.5rem' : '0.75rem'
                              }}
                              onFocus={(e) => {
                                e.currentTarget.style.borderColor = '#3b82f6';
                                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                              }}
                              onBlur={(e) => {
                                e.currentTarget.style.borderColor = '#e2e8f0';
                                e.currentTarget.style.boxShadow = 'none';
                              }}
                              onMouseEnter={(e) => {
                                if (document.activeElement !== e.currentTarget) {
                                  e.currentTarget.style.borderColor = '#cbd5e1';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (document.activeElement !== e.currentTarget) {
                                  e.currentTarget.style.borderColor = '#e2e8f0';
                                }
                              }}
                            >
                              <option value="10">10</option>
                              <option value="25">25</option>
                              <option value="50">50</option>
                              <option value="100">100</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ) : (
            <PatientList
              patients={patients}
              isLoading={isLoading}
              error={error}
              onRetry={() => {
                if (searchQuery) {
                  refetchSearch();
                }
              }}
            />
          )}
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={t('patients.addPatient')}
      >
        <PatientForm
          onSubmit={handleCreatePatient}
          isLoading={createMutation.isPending}
        />
      </Modal>

      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditPatientId(null);
        }}
        title={t('patients.editPatient', 'Edit Patient')}
      >
        {isLoadingEditPatient ? (
          <div className="d-flex justify-content-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
          </div>
        ) : editPatient ? (
          <PatientForm
            patient={editPatient}
            onSubmit={handleUpdatePatient}
            isLoading={updateMutation.isPending}
          />
        ) : (
          <div className="text-center py-5">
            <p className="text-muted">{t('errors.required', 'Patient not found')}</p>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={deletePatientId !== null}
        onClose={() => setDeletePatientId(null)}
        onConfirm={handleDeletePatient}
        title={t('common.delete')}
        message={t('common.delete') + ' ' + t('patients.title')}
        confirmText={t('common.delete')}
        variant="danger"
      />
    </div>
  );
};
